import { Navigate, Outlet, useLocation, useMatch } from "react-router-dom";
import PermissionGate from "@/core/auth/PermissionGate";
import { useSectorPermissions } from "@/core/auth/useSectorPermissions";
import { RoutePaths } from "@/core/constants/RoutePaths";
import Loading from "@/shared/components/Loading";
import { useActiveCompanyAdmin } from "@/features/settings/hooks/useActiveCompanyAdmin";
import { PERSONAL_SETTINGS_PATHS } from "@/features/settings/settingsNav";

/**
 * Gate das Configurações. As abas do setor exigem `ui:settings:view` no setor
 * ativo, mas Faturamento é da EMPRESA (admin de qualquer setor dela). Sem essa
 * exceção, o admin da empresa que é Membro no setor ativo caía no bloqueio de
 * Configurações ao voltar do Portal do Stripe (`return_url=/settings/billing`)
 * ou pelo link da régua (`/settings/billing?upsell=expired`).
 *
 * Em `/settings/billing*` quem decide é só o papel na empresa, com ou sem a
 * chave, igual à visibilidade da aba: admin da empresa abre (a SettingsPage
 * mostra só Faturamento quando falta a chave); quem tem a chave mas não é admin
 * da empresa vai para Avançado, como o próprio BillingSettingsTab já fazia;
 * quem não tem nenhum dos dois cai no PermissionGate de sempre.
 *
 * Retenção de dados (SA-1433) é da empresa e segue a regra de Faturamento.
 *
 * Notificações (SA-1348) e Respostas rápidas (SA-1426) são da pessoa: abrem
 * para qualquer perfil, e é para Notificações que vai quem, sem a chave, pede uma aba do setor (`/settings`
 * inclusive). Com as permissões ainda carregando ou com erro, quem decide é o
 * PermissionGate de sempre.
 */
export function SettingsGate() {
  const {
    permissions,
    isLoading: isPermissionsLoading,
    isPlaceholder: isPermissionsPlaceholder,
    isError: isPermissionsError,
  } = useSectorPermissions();
  const { isCompanyAdmin, isLoading: isCompanyAdminLoading } =
    useActiveCompanyAdmin();
  const isBillingRoute =
    useMatch({ path: RoutePaths.SETTINGS_BILLING, end: false }) !== null;
  // SA-1433: Retenção de dados é da empresa, com a mesma regra de Faturamento.
  const isDataRetentionRoute =
    useMatch({ path: RoutePaths.SETTINGS_DATA_RETENTION, end: false }) !== null;
  const isCompanyAdminRoute = isBillingRoute || isDataRetentionRoute;
  const { pathname } = useLocation();
  const isPersonalRoute = PERSONAL_SETTINGS_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );

  if (isPersonalRoute) {
    // Rota pessoal abre para todos, mas as abas da página dependem da chave:
    // decidir antes das permissões trocaria a barra de abas na frente da
    // pessoa. Erro nas permissões não segura (como em Faturamento).
    if (
      (isPermissionsLoading || isPermissionsPlaceholder) &&
      !isPermissionsError
    ) {
      return <Loading />;
    }
    return <Outlet />;
  }

  if (isCompanyAdminRoute) {
    // Espera o papel na empresa (decide a rota) e as permissões do setor
    // (decidem as abas): decidir antes piscaria o bloqueio ou trocaria as abas.
    // Erro nas permissões não segura: Faturamento não depende delas.
    if (
      isCompanyAdminLoading ||
      ((isPermissionsLoading || isPermissionsPlaceholder) &&
        !isPermissionsError)
    ) {
      return <Loading />;
    }
    if (isCompanyAdmin) {
      return <Outlet />;
    }
    if (permissions.includes("ui:settings:view")) {
      return (
        <Navigate
          to={RoutePaths.SETTINGS_ADVANCED}
          replace
        />
      );
    }
  }

  const permissionsSettled =
    !isPermissionsLoading && !isPermissionsPlaceholder && !isPermissionsError;
  if (permissionsSettled && !permissions.includes("ui:settings:view")) {
    return (
      <Navigate
        to={RoutePaths.SETTINGS_NOTIFICATIONS}
        replace
      />
    );
  }

  return <PermissionGate requiredPermission="ui:settings:view" />;
}

export default SettingsGate;
